import { useEffect, useMemo, useState } from 'react';
import { Send, User, Mail, Phone, Lock, Home, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ProjectContactFormProps {
  projectId: string;
  projectName?: string;
  referenceCode?: string;
  assignedAgentId?: string | null;
  onSuccess?: () => void;
}

interface UnitType {
  id: string;
  name: string;
  bedrooms: number | null;
  area_min: number | null;
  area_max: number | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  buyer: 'Buyer / Tenant',
  agent: 'Agent',
  other: 'Other',
};

const formatUnitLabel = (u: UnitType) =>
  `${u.name}${u.bedrooms ? ` · ${u.bedrooms}BR` : ''}${u.area_min ? ` · ${u.area_min}${u.area_max && u.area_max !== u.area_min ? `–${u.area_max}` : ''} m²` : ''}`;

export function ProjectContactForm({
  projectId, projectName, referenceCode, assignedAgentId, onSuccess,
}: ProjectContactFormProps) {
  const { user, profile } = useAuth();
  const refId = (referenceCode || `ID${projectId.slice(0, 8)}`).toUpperCase();
  const displayName = projectName || 'this project';

  const [units, setUnits] = useState<UnitType[]>([]);
  const [unitId, setUnitId] = useState('');
  const [marketedBy, setMarketedBy] = useState<{ name: string; logoUrl: string | null } | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [msgEdited, setMsgEdited] = useState(false);
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const selectedUnit = useMemo(() => units.find(u => u.id === unitId), [units, unitId]);

  useEffect(() => {
    if (!user) return;
    const meta: any = (user as any)?.user_metadata || {};
    setName(n => n || (profile as any)?.full_name || (profile as any)?.name || meta.full_name || meta.name || '');
    setPhone(p => p || (profile as any)?.phone || meta.phone || '');
    setEmail(e => e || user.email || meta.email || '');
  }, [profile, user]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('project_unit_types')
        .select('id, name, bedrooms, area_min, area_max, price_min, price_max, currency')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });
      setUnits(data || []);
    })();
  }, [projectId]);

  // Resolve marketed-by (developer or marketing agency) for logo header
  useEffect(() => {
    (async () => {
      const { data: proj } = await supabase
        .from('projects')
        .select('developer_id, marketing_agency_id, developer_profiles(company_name, logo_url), marketing_agency:agencies!projects_marketing_agency_id_fkey(name, logo_url)')
        .eq('id', projectId)
        .maybeSingle();
      if (!proj) return;
      const dev: any = (proj as any).developer_profiles;
      const ma: any = (proj as any).marketing_agency;
      if (dev?.company_name) {
        setMarketedBy({ name: dev.company_name, logoUrl: dev.logo_url || null });
      } else if (ma?.name) {
        setMarketedBy({ name: ma.name, logoUrl: ma.logo_url || null });
      }
    })();
  }, [projectId]);

  useEffect(() => {
    if (msgEdited) return;
    const roleIntro: Record<string, string> = {
      buyer: 'I am a Buyer / Tenant',
      agent: 'I am an Agent representing a client',
      other: 'I am reaching out',
    };
    setMessage(`Hello, ${roleIntro[role] || roleIntro.buyer} and I'd like to reserve a unit at ${displayName} (${refId}). Selected unit: ${selectedUnit?.name || '—'}. Please contact me with availability and next steps.`);
  }, [selectedUnit, displayName, refId, msgEdited, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      const roleLabel = ROLE_LABELS[role] || 'Buyer / Tenant';
      const finalMessage = `[Contact type: ${roleLabel}]\n\n${message.trim()}\n\n[Unit: ${selectedUnit?.name || '—'} | Project Ref: ${refId}]`;
      const { error } = await supabase.functions.invoke('lead-management', {
        body: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || null,
          message: finalMessage,
          project_id: projectId,
          source: 'website',
          agent_id: assignedAgentId || null,
        },
      });
      if (error) throw error;
      toast.success('Your message has been sent!');
      setMsgEdited(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5">
      {marketedBy && (
        <div className="flex flex-col items-center text-center pb-4 mb-1 border-b">
          {marketedBy.logoUrl ? (
            <img
              src={marketedBy.logoUrl}
              alt={marketedBy.name}
              className="h-16 w-auto max-w-[140px] object-contain mb-2"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-2">
              <Building2 className="h-7 w-7 text-primary" />
            </div>
          )}
          <div className="text-[10px] font-semibold text-primary uppercase tracking-wide">
            Marketed by
          </div>
          <div className="font-bold text-sm leading-tight">{marketedBy.name}</div>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="truncate">{displayName}</span>
        <Badge variant="outline" className="font-mono text-[10px] shrink-0">{refId}</Badge>
      </div>


      {units.length > 0 && (
        <div>
          <Label className="text-xs font-semibold mb-1.5 block">
            Available Units <span className="text-destructive">*</span>
          </Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="h-12">
              <div className="flex items-center gap-2">
                <Home className="h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
                <SelectValue placeholder="Select a unit (Apartment, Shop, Office Space…)" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {units.map(u => (
                <SelectItem key={u.id} value={u.id}>{formatUnitLabel(u)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="relative">
        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
        <Input
          required
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your Name *"
          className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
        />
      </div>

      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email Address"
          className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
        />
      </div>

      <div className="relative">
        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground/70" strokeWidth={1.75} />
        <Input
          type="tel"
          required
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="Phone Number *"
          className="pl-10 h-12 text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal"
        />
      </div>

      <Textarea
        rows={3}
        value={message}
        onChange={e => { setMessage(e.target.value); setMsgEdited(true); }}
        placeholder="How can we help you?"
        className="resize-none text-sm font-normal placeholder:text-muted-foreground/60 placeholder:font-normal py-3 px-3.5"
      />

      <div className="space-y-2 pt-1">
        <Label className="text-xs font-semibold">I am a <span className="text-destructive">*</span></Label>
        <RadioGroup value={role} onValueChange={setRole} className="flex gap-4 flex-wrap">
          {Object.entries(ROLE_LABELS).map(([value, label]) => (
            <div key={value} className="flex items-center gap-1.5">
              <RadioGroupItem value={value} id={`pcf-role-${value}`} />
              <Label htmlFor={`pcf-role-${value}`} className="text-xs cursor-pointer">{label}</Label>
            </div>
          ))}
        </RadioGroup>
        <p className="text-[11px] text-muted-foreground">
          The agent will be informed of your role when you submit this inquiry.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="pcf-keep-informed" checked={keepInformed} onCheckedChange={c => setKeepInformed(!!c)} />
        <Label htmlFor="pcf-keep-informed" className="text-xs cursor-pointer">
          Keep me informed about similar projects
        </Label>
      </div>

      <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
        <Send className="h-4 w-4" />
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>

      <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1 pt-1">
        <Lock className="h-3 w-3" />
        Your information is secure and will not be shared.
      </p>
    </form>
  );
}
